import { workLogEntryIsToolLike } from "../../session-logic";
import { workEntryDisplayLabel } from "./MessagesTimeline.labels";
import { formatWorkspaceRelativePath } from "../../filePathDisplay";
import { workEntryViewedImagePath } from "../../workLogToolGroups";
import { getQuestionAnswerPreview, hasQuestionAnswer } from "../../workLogUserInput";
import { formatTimestamp } from "../../timestampFormat";
import { ChevronRightIcon } from "lucide-react";
import { useState, use, type KeyboardEvent, type MouseEvent } from "react";
import { cn } from "~/lib/utils";
import { TimelineRowCtx } from "./TimelineRowContext";
import { GitHubActionRequiredRow, GitHubToolConfirmationRow } from "./TimelineGitHubRows";
import {
  ToolActivityIconView,
  workEntryIconName,
  workToneIcon,
  type TimelineWorkEntry,
} from "./TimelineWorkEntryIcons";

function workEntryRawCommand(
  workEntry: Pick<TimelineWorkEntry, "command" | "rawCommand">,
): string | null {
  const rawCommand = workEntry.rawCommand?.trim();
  if (!rawCommand || !workEntry.command) {
    return null;
  }
  return rawCommand === workEntry.command.trim() ? null : rawCommand;
}

/**
 * The expanded row body (ticket 3525, upstream `buildToolCallExpandedBody`):
 * the command output, raw command, MCP call payload, and changed files a
 * collapsed row summarizes, joined in upstream's order and deduplicated
 * against the visible label.
 */
export function buildToolCallExpandedBody(
  workEntry: TimelineWorkEntry,
  workspaceRoot: string | undefined,
  visibleLabel: string,
  viewedImagePath: string | null,
): string | null {
  const blocks: string[] = [];
  const seen = new Set<string>([visibleLabel.trim()]);
  const addBlock = (value: string | null | undefined) => {
    const text = value?.trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    blocks.push(text);
  };
  if (workEntry.itemType === "mcp_tool_call" && workEntry.toolData !== undefined) {
    addBlock(`MCP call\n${JSON.stringify(workEntry.toolData, null, 2)}`);
  }
  const command = workEntry.command?.trim();
  const raw = workEntryRawCommand(workEntry);
  if (command === visibleLabel.trim()) {
    seen.add(command);
  } else {
    addBlock(raw ?? command);
  }
  const detail = workEntry.detail?.trim();
  if (detail !== viewedImagePath?.trim()) {
    addBlock(detail);
  }
  const viewedImagePaths = new Set(
    viewedImagePath
      ? [viewedImagePath.trim(), formatWorkspaceRelativePath(viewedImagePath, workspaceRoot)]
      : [],
  );
  const changedFiles = (workEntry.changedFiles ?? []).flatMap((filePath) => {
    const formattedPath = formatWorkspaceRelativePath(filePath, workspaceRoot);
    return viewedImagePaths.has(filePath) ||
      viewedImagePaths.has(formattedPath) ||
      filePath.trim() === detail ||
      formattedPath === detail
      ? []
      : [formattedPath];
  });
  if (changedFiles.length > 0) {
    addBlock([...new Set(changedFiles)].join("\n"));
  }
  return blocks.length > 0 ? blocks.join("\n\n") : null;
}

export const toolCallExpandedBodyClassName =
  "max-h-64 cursor-text overflow-auto whitespace-pre-wrap break-words font-mono text-secondary-label text-xs leading-relaxed select-text";

const stopRowToggle = (e: { stopPropagation: () => void }) => e.stopPropagation();

/**
 * Click handler for expanded row labels, which turn text selection back on.
 * Only a click that ends a real selection is withheld from the row toggle, so
 * an ordinary click on the label still bubbles and collapses the row it opened.
 */
const stopRowToggleWhileSelectingText = (e: MouseEvent<HTMLElement>) => {
  const selection = e.currentTarget.ownerDocument.getSelection();
  if (selection && !selection.isCollapsed) {
    e.stopPropagation();
  }
};

// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.tsx at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function SimpleWorkEntryRow(props: {
  workEntry: TimelineWorkEntry;
  workspaceRoot: string | undefined;
  isExpandedToolGroupEntry: boolean;
  displayLabel?: string | undefined;
  onToggleEntry?: ((collapsed: boolean) => void) | undefined;
}) {
  const { workEntry, workspaceRoot, isExpandedToolGroupEntry, displayLabel } = props;
  const { timestampFormat } = use(TimelineRowCtx);
  // The React Compiler memoizes this row; useState must stay above the early
  // returns so hook order holds when an entry gains or loses a blocker field.
  const [expanded, setExpanded] = useState(false);
  // Forge keeps its brokered GitHub blocker cards (ticket 3525 adaptation):
  // promoted entries render as approval cards, not generic tool rows.
  if (workEntry.githubConfirmation) {
    return (
      <GitHubToolConfirmationRow
        confirmation={workEntry.githubConfirmation}
        createdAt={workEntry.createdAt}
      />
    );
  }
  if (workEntry.githubActionRequired) {
    return (
      <GitHubActionRequiredRow
        actionRequired={workEntry.githubActionRequired}
        createdAt={workEntry.createdAt}
      />
    );
  }
  const previewText = displayLabel ?? workEntryDisplayLabel(workEntry, workspaceRoot);
  const answerPreview = workEntry.questionAnswer
    ? getQuestionAnswerPreview(workEntry.questionAnswer)
    : null;
  const canExpand =
    Boolean(workEntry.questionAnswer) ||
    Boolean(
      workEntryRawCommand(workEntry) ||
      workEntry.command?.trim() ||
      workEntry.detail?.trim() ||
      workEntry.changedFiles?.length ||
      workEntryViewedImagePath(workEntry),
    );
  const viewedImagePath = workEntryViewedImagePath(workEntry);
  const expandedBody = expanded
    ? buildToolCallExpandedBody(workEntry, workspaceRoot, previewText, viewedImagePath)
    : null;
  const iconConfig = workToneIcon(workEntry.tone);
  const accessiblePreview = [previewText, answerPreview].filter(Boolean).join(": ");
  const toggleExpanded = () => {
    const next = !expanded;
    props.onToggleEntry?.(!next);
    setExpanded(next);
  };

  return (
    <div
      className={cn(
        "group/timeline-row relative flex flex-col rounded-md px-0.5 transition-colors",
        isExpandedToolGroupEntry ? "py-0" : "py-0.5",
        expanded && "mb-1",
        canExpand &&
          "cursor-pointer hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70",
      )}
      role={canExpand ? "button" : undefined}
      tabIndex={canExpand ? 0 : undefined}
      aria-label={accessiblePreview || undefined}
      aria-expanded={canExpand ? expanded : undefined}
      onClick={canExpand ? toggleExpanded : undefined}
      onKeyDown={
        canExpand
          ? (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleExpanded();
              }
            }
          : undefined
      }
    >
      <div className="flex select-none items-center gap-1.5 transition-[opacity,translate] duration-200">
        <span
          className={cn(
            "flex size-6 shrink-0 items-center justify-center",
            workEntry.tone === "tool" ? "text-icon-muted" : iconConfig.className,
          )}
        >
          <ToolActivityIconView
            icon={workEntry.toolIcon}
            fallbackName={workEntryIconName(workEntry)}
            className="block size-4 shrink-0 stroke-2"
            muted
          />
        </span>
        <div className="min-w-0 flex-1 overflow-hidden">
          <p className="flex min-w-0 w-full items-baseline gap-1.5 text-sm leading-relaxed">
            <span
              className={cn(
                answerPreview ? "shrink-0" : "min-w-0 flex-1",
                expanded ? "whitespace-pre-wrap break-words select-text" : "truncate",
                workLogEntryIsToolLike(workEntry) ? "text-secondary-label" : "text-foreground/80",
              )}
              onClick={expanded ? stopRowToggleWhileSelectingText : undefined}
            >
              {previewText}
            </span>
            {answerPreview ? (
              <span
                className={cn(
                  "min-w-0 truncate",
                  !expanded &&
                    workEntry.questionAnswer &&
                    hasQuestionAnswer(workEntry.questionAnswer)
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {answerPreview}
              </span>
            ) : null}
          </p>
        </div>
        <span className="shrink-0 font-mono text-2xs tabular-nums text-muted-foreground/70">
          {formatTimestamp(workEntry.createdAt, timestampFormat)}
        </span>
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center",
            !canExpand && "invisible",
          )}
          aria-hidden
        >
          <ChevronRightIcon
            className={cn(
              "size-3 shrink-0 text-icon-muted opacity-70 transition-transform duration-200",
              expanded && "rotate-90",
            )}
          />
        </span>
      </div>
      {expanded && canExpand && expandedBody ? (
        <div
          className="mt-1 ms-7 cursor-default rounded-md bg-muted/40 px-3 py-2"
          onClick={stopRowToggle}
          onPointerDown={stopRowToggle}
        >
          <pre className={toolCallExpandedBodyClassName}>{expandedBody}</pre>
        </div>
      ) : null}
    </div>
  );
}
